import { useState, useEffect, useMemo, useRef } from 'react';
import { usePageTitle } from '@/context/PageTitleContext';
import { useRepairs } from '@/hooks/useRepairs';
import { useTechnicians } from '@/hooks/useTechnicians';
import { useParts } from '@/hooks/useParts';
import { useAuth } from '@/hooks/useAuth';
import AddRepairModal from './components/AddRepairModal';
import CreateTicketInvoiceModal from './components/CreateTicketInvoiceModal';
import AddTicketPartModal from './components/AddTicketPartModal';
import { getTicketParts, removeTicketPart, type TicketPart } from '@/services/wireless/ticketParts';
import { getTicketComments, addTicketComment, requestReassignment, requestAdditionalApproval, type TicketComment } from '@/services/wireless/ticketComments';
import SearchDropdown from '@/components/shared/SearchDropdown';
import Pagination from '@/components/shared/Pagination';
import DateRangePicker, { type DateRange } from '@/components/shared/DateRangePicker';
import {
  X, Clock, Shield, Plus, Pencil, Trash2, Camera, Video, AlertCircle, Loader2,
  CheckCircle2, Scissors, Stethoscope, ArrowRightCircle, UserX, Users, XCircle, Zap, Printer, AlertTriangle, Images,
} from 'lucide-react';
import type { Repair, RepairStatus, RepairMediaStage, RepairMediaUploadInput } from '@/types/repair';
import type { Payment } from '@/types/wireless';
import {
  REPAIR_STATUS_META as STATUS, activePipeline, pipelineStep,
  nextAction, nextStatus, isActiveRepairStatus, isDiagnosisStage, isOverdueRepair,
  statusToMediaStage, statusToServiceStage, requiredMediaStageForAdvance, REQUIRED_MEDIA_STAGE_LABEL,
} from '@/utils/repairStatus';
import { formatDate } from '@/utils/date';
import { getPaymentsForTicket } from '@/services/wireless/payments';
import { ensureTicketInvoice } from '@/services/wireless/autoInvoice';
import { useTaxSettings } from '@/hooks/useTaxSettings';
import { hasConfirmedDiagnosisPayment, getSignedMediaUrls } from '@/services/repairs';
import { errMessage } from '@/utils/errors';
import { useWirelessSettings } from '@/hooks/useWirelessSettings';
import { downloadTicketReceiptPdf } from '@/utils/ticketReceiptPdf';
import { useReassignmentRequests } from '@/hooks/useReassignmentRequests';
import ReassignmentRequestsBanner from '@/components/shared/ReassignmentRequestsBanner';
import { useApprovalRequests } from '@/hooks/useApprovalRequests';
import ApprovalRequestsBanner from '@/components/shared/ApprovalRequestsBanner';
import StaleTicketsBanner from '@/components/shared/StaleTicketsBanner';
import EtaRemindersBanner from '@/components/shared/EtaRemindersBanner';

const PAGE_SIZE = 12;

// started is either a bare YYYY-MM-DD or a full timestamptz ISO string
function fmtStarted(started: string): string {
  if (!started) return '—';
  const hasTime = started.includes('T');
  return formatDate(hasTime ? started : `${started}T00:00`);
}

// All assignees are equal — no "primary" to single out — so this just joins
// names, truncating to keep the tight card layout from overflowing.
function formatTechnicians(technicians: Repair['technicians'], maxNames = 2): string {
  if (!technicians.length) return '';
  if (technicians.length <= maxNames) return technicians.map(t => t.name).join(', ');
  const shown = technicians.slice(0, maxNames).map(t => t.name).join(', ');
  return `${shown} +${technicians.length - maxNames}`;
}

const FILTER_TABS: { key: string; label: string }[] = [
  { key: 'all',           label: 'All' },
  { key: 'unassigned',    label: 'Unassigned' },
  { key: 'in_queue',      label: 'In Queue' },
  { key: 'in_progress',   label: 'In Progress' },
  { key: 'ready',         label: 'Ready' },
  { key: 'completed',     label: 'Done' },
];

// ── Repair Card ───────────────────────────────────────────────────────────

function RepairCard({ repair, onClick, selected }: {
  repair: Repair; onClick: () => void; selected: boolean;
}) {
  const { user } = useAuth();
  // Technicians only ever work their own assigned tickets — pricing and the
  // customer's identity are a reception/admin concern, not theirs to see.
  const isTechnicianScoped = !!user?.scopeTicketsToTechnician;
  const s = STATUS[repair.status] ?? STATUS.received;
  return (
    <div
      onClick={onClick}
      className="rounded-2xl p-4 cursor-pointer transition-all select-none"
      style={{
        background: 'hsl(var(--card))',
        border: `1px solid ${selected ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
        boxShadow: selected ? '0 0 0 2px rgba(236,1,24,0.15)' : 'none',
      }}
      onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLElement).style.borderColor = 'hsl(var(--muted-foreground)/0.4)'; }}
      onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLElement).style.borderColor = 'hsl(var(--border))'; }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[11px] font-mono" style={{ color: 'hsl(var(--muted-foreground))' }}>{repair.id}</span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0"
          style={{ background: s.bg, color: s.color }}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.dot }} />
          {s.label}
        </span>
      </div>

      <p className="text-sm font-bold mb-0.5" style={{ color: 'hsl(var(--foreground))' }}>{repair.device}</p>
      <p className="text-xs mb-3" style={{ color: 'hsl(var(--muted-foreground))' }}>{repair.issue}</p>

      <div className="flex items-center justify-between mb-2">
        <span className="text-xs truncate" style={{ color: 'hsl(var(--muted-foreground))' }}>{repair.customer}</span>
        {repair.technicians.length > 0 ? (
          <span className={`text-xs ${isTechnicianScoped ? 'ml-auto' : ''}`} style={{ color: 'hsl(var(--muted-foreground))' }}>Tech: {formatTechnicians(repair.technicians)}</span>
        ) : (
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${isTechnicianScoped ? 'ml-auto' : ''}`} style={{ color: '#f59e0b' }}>
            <UserX className="w-3 h-3" /> Unassigned
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3 shrink-0" style={{ color: isOverdueRepair(repair) ? '#ef4444' : 'hsl(var(--muted-foreground))' }} />
          <span className="text-xs" style={{ color: isOverdueRepair(repair) ? '#ef4444' : 'hsl(var(--muted-foreground))' }}>
            {isOverdueRepair(repair) ? 'Overdue: ' : 'ETA: '}{repair.eta || '—'}
          </span>
        </div>
        {!isTechnicianScoped && (
          <span className="text-xs font-bold" style={{ color: 'hsl(var(--foreground))' }}>
            {repair.cost || 'TBD'}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mt-2">
        {repair.jobType === 'diagnosis_only' && (
          <div className="flex items-center gap-1">
            <Stethoscope className="w-3 h-3" style={{ color: '#0ea5e9' }} />
            <span className="text-[10px] font-semibold" style={{ color: '#0ea5e9' }}>Dx Only</span>
          </div>
        )}
        {repair.jobType === 'straight_repair' && (
          <div className="flex items-center gap-1">
            <Zap className="w-3 h-3" style={{ color: '#22c55e' }} />
            <span className="text-[10px] font-semibold" style={{ color: '#22c55e' }}>Straight Repair</span>
          </div>
        )}
        {repair.warranty && (
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3" style={{ color: '#6366f1' }} />
            <span className="text-[10px] font-medium" style={{ color: '#6366f1' }}>Under Warranty</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────────────

export function RepairDetailPanel({ repair, onClose, onUpdateStatus, onAddNote, onAddMedia, onRemoveMedia, uploaderName, canManageTickets, canUpdateProgress, canMarkCollected = false, canDeleteTickets, onProceedToRepair, onCloseDiagnosisOnly, onDiscontinue, onPatchParts, onEdit, onDelete }: {
  repair: Repair;
  onClose: () => void;
  onUpdateStatus: (id: string, s: RepairStatus) => void;
  onAddNote: (id: string, note: string) => void;
  onAddMedia: (id: string, input: RepairMediaUploadInput) => Promise<unknown>;
  onRemoveMedia: (id: string, mediaId: string) => Promise<unknown>;
  uploaderName?: string;
  canManageTickets: boolean;
  /** Receptionists can create/edit/assign a ticket but never advance its status through the repair pipeline — that's reserved for the assigned technician and admin. The one exception is the final handoff, gated separately by `canMarkCollected`. */
  canUpdateProgress: boolean;
  /** The ready → completed step ("Mark Collected") is the pickup handoff, not repair progress — reception's call, not the technician's. Defaults to false so a caller that hasn't been updated (e.g. an older embed) never grants it by omission. */
  canMarkCollected?: boolean;
  /** Deleting a ticket is admin-only — receptionist can create/edit but not delete. */
  canDeleteTickets: boolean;
  onProceedToRepair: (id: string) => void;
  onCloseDiagnosisOnly: (id: string) => void;
  /** Ends the job without a completed repair — declined after diagnosis
   *  mid-repair, or a customer backing out of new work found on a return
   *  visit. Distinct from "Close — Diagnosis Only" which only applies
   *  before any repair work starts. */
  onDiscontinue: (id: string, reason: string) => void;
  /** Parts a technician flags as likely needed — a lightweight suggestion
   *  list, distinct from the stock-deducting "Parts Used" section below it. */
  onPatchParts: (id: string, parts: NonNullable<Repair['parts']>) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [promptStage, setPromptStage] = useState<RepairMediaStage | null>(null);
  const [ticketPayments, setTicketPayments] = useState<Payment[]>([]);
  const [linkedInvoiceNumber, setLinkedInvoiceNumber] = useState<string | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [ticketParts, setTicketParts] = useState<TicketPart[]>([]);
  const [showAddPart, setShowAddPart] = useState(false);
  const [addingRecommendedPart, setAddingRecommendedPart] = useState(false);
  const [recommendedPartName, setRecommendedPartName] = useState('');
  const [checkingInvoice, setCheckingInvoice] = useState(false);
  const [internalComments, setInternalComments] = useState<TicketComment[]>([]);
  const [addingComment, setAddingComment] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [discontinuing, setDiscontinuing] = useState(false);
  const [waivingPhoto, setWaivingPhoto] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [waiveSaving, setWaiveSaving] = useState(false);
  const [discontinueReason, setDiscontinueReason] = useState('');
  const [requestingReassign, setRequestingReassign] = useState(false);
  const [reassignReason, setReassignReason] = useState('');
  const [reassignSaving, setReassignSaving] = useState(false);
  const [reportingExtra, setReportingExtra] = useState(false);
  const [extraReason, setExtraReason] = useState('');
  const [extraAmount, setExtraAmount] = useState('');
  const [extraSaving, setExtraSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const { user } = useAuth();
  const { technicians } = useTechnicians();
  // Technicians/admins as before, plus any role explicitly granted
  // ticket_parts:create (reception logs the part after the tech confirms it).
  const canLogParts = canUpdateProgress || (user?.permissions ?? []).includes('ticket_parts:create');
  const canUploadMedia = user?.role === 'technician' && technicians.some(tech =>
    tech.profile_id === user.id && repair.technicians.some(assigned => assigned.id === tech.id),
  );
  // Technicians repair the device — what the customer's charged, who the
  // customer is, and whether/how they've paid are a reception/admin
  // concern, not shown on their view of the ticket. Just the repair's
  // progress.
  const isTechnicianScoped = !!user?.scopeTicketsToTechnician;
  const s = STATUS[repair.status] ?? STATUS.received;
  const isDxOnly = repair.jobType === 'diagnosis_only';
  const isStraightRepair = repair.jobType === 'straight_repair';
  const pipeline = activePipeline(repair.status, repair.jobType);
  const currentStep = pipelineStep(repair.status, repair.jobType);
  const isDone = ['completed', 'cancelled'].includes(repair.status);
  const isAwaitingDecision = repair.status === 'awaiting_approval';
  const isClosedDiagnosis = repair.status === 'diagnosis_only_closed';
  const media = repair.media ?? [];
  const [signedMediaUrls, setSignedMediaUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    const paths = media.map(m => m.path).filter((p): p is string => !!p);
    if (paths.length === 0) { setSignedMediaUrls({}); return; }
    let cancelled = false;
    getSignedMediaUrls(paths).then(map => { if (!cancelled) setSignedMediaUrls(map); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.map(m => m.path).join(',')]);
  const { settings } = useWirelessSettings();
  const { parts: inventoryParts } = useParts();
  const { taxEnabled, vatRate, nhilGetfundRate } = useTaxSettings();
  // Only meaningful once there's actually a finished job to hand back —
  // matches the same "ready or later" window Create Invoice already uses.
  const canPrintReceipt = ['ready', 'completed', 'diagnosis_only_closed'].includes(repair.status);
  // Same unconditional policy as invoices — every repair carries the
  // shop's standard warranty, not a per-ticket toggle.
  const handlePrintReceipt = () => downloadTicketReceiptPdf({
    repair,
    warrantyDays: settings?.warranty_days,
    settings: settings ?? undefined,
  });

  const upcomingStatus = nextStatus(repair.status, repair.jobType);
  // The pickup handoff — ready → completed — is reception's call, not the
  // technician's; every earlier step in the pipeline is the other way
  // around. Gated separately below via canMarkCollected vs canUpdateProgress.
  const isMarkCollectedStep = repair.status === 'ready';
  const requiredStage = requiredMediaStageForAdvance(repair.status, upcomingStatus);
  const hasRequiredPhoto = !requiredStage || media.some(m => m.stage === requiredStage);
  const depositPaid = ticketPayments.reduce((s, p) => s + p.amount, 0);
  // A paid diagnosis fee is billable the moment it's paid, not just once the
  // job reaches Ready — subsumes the old diagnosis_only_closed/cancelled
  // special cases, since neither is reachable without one. A straight
  // repair has no diagnosis fee at all, so it's billable (unpaid, for the
  // quoted cost) from the moment it's created instead.
  const readyForInvoice = repair.status === 'ready' || hasConfirmedDiagnosisPayment(repair) || repair.jobType === 'straight_repair';

  useEffect(() => {
    let cancelled = false;
    if (!repair.ticketDbId) { setTicketPayments([]); return; }
    getPaymentsForTicket(repair.ticketDbId).then(rows => { if (!cancelled) setTicketPayments(rows); }).catch(() => { if (!cancelled) setTicketPayments([]); });
    return () => { cancelled = true; };
  }, [repair.ticketDbId]);

  const loadTicketParts = () => {
    if (!repair.ticketDbId) { setTicketParts([]); return; }
    getTicketParts(repair.ticketDbId).then(setTicketParts).catch(() => setTicketParts([]));
  };

  useEffect(loadTicketParts, [repair.ticketDbId]);

  const loadComments = () => {
    if (!repair.ticketDbId) { setInternalComments([]); return; }
    getTicketComments(repair.ticketDbId).then(setInternalComments).catch(() => setInternalComments([]));
  };

  useEffect(loadComments, [repair.ticketDbId]);

  const handleAddComment = async () => {
    const body = commentText.trim();
    if (!body || !repair.ticketDbId) return;
    setCommentSaving(true);
    try {
      await addTicketComment(repair.ticketDbId, body, user?.name ?? 'Staff');
      setCommentText('');
      setAddingComment(false);
      loadComments();
    } catch (e) {
      alert(errMessage(e, 'Failed to add note'));
    } finally {
      setCommentSaving(false);
    }
  };

  const handleConfirmDiscontinue = () => {
    const reason = discontinueReason.trim();
    if (!reason) return;
    onDiscontinue(repair.id, reason);
    setDiscontinuing(false);
    setDiscontinueReason('');
  };

  // A technician can't reassign a ticket themselves — this logs the ask as
  // a tagged request so it surfaces on Reception's and Admin's Tickets
  // Reassignment Requests banner (not just buried in Internal Notes),
  // same "request, someone else applies it" shape as Discontinue.
  const handleRequestReassign = async () => {
    const reason = reassignReason.trim();
    if (!reason || !repair.ticketDbId) return;
    setReassignSaving(true);
    try {
      await requestReassignment(repair.ticketDbId, reason, user?.name ?? 'Staff');
      setRequestingReassign(false);
      setReassignReason('');
      loadComments();
    } catch (e) {
      alert(errMessage(e, 'Failed to send reassignment request'));
    } finally {
      setReassignSaving(false);
    }
  };

  // A technician can't notify or get sign-off from the customer directly —
  // this logs the ask the same tagged way as Request Reassignment, so it
  // surfaces on Reception's/Admin's approval queue instead of only living
  // in Internal Notes. The existing awaiting_approval status only covers
  // the initial diagnosis decision; this covers something found later,
  // mid-repair.
  const handleRequestApproval = async () => {
    const reason = extraReason.trim();
    if (!reason || !repair.ticketDbId) return;
    setExtraSaving(true);
    try {
      const amount = extraAmount.trim() ? parseFloat(extraAmount) : null;
      await requestAdditionalApproval(repair.ticketDbId, reason, amount, user?.name ?? 'Staff');
      setReportingExtra(false);
      setExtraReason('');
      setExtraAmount('');
      loadComments();
    } catch (e) {
      alert(errMessage(e, 'Failed to send approval request'));
    } finally {
      setExtraSaving(false);
    }
  };

  const handleRemoveTicketPart = async (part: TicketPart) => {
    if (!confirm(`Remove ${part.part_name} (×${part.quantity})? Any stock already deducted for it is restored.`)) return;
    try {
      await removeTicketPart(part.id);
      loadTicketParts();
    } catch (e) {
      alert(errMessage(e, 'Failed to remove part'));
    }
  };

  // A recommendation is just a name + status flag, not linked to a real
  // inventory row — unlike "Parts Used" below, it never touches stock.
  const handleAddRecommendedPart = () => {
    const name = recommendedPartName.trim();
    if (!name) return;
    onPatchParts(repair.id, [...(repair.parts ?? []), { name, status: 'pending' }]);
    setRecommendedPartName('');
    setAddingRecommendedPart(false);
  };

  const handleRemoveRecommendedPart = (index: number) => {
    onPatchParts(repair.id, (repair.parts ?? []).filter((_, i) => i !== index));
  };

  useEffect(() => {
    let cancelled = false;
    // Technicians can't create invoices (RLS rejects it) and never see
    // invoice state, so don't fire a request that can only fail.
    if (!repair.ticketDbId || !readyForInvoice || isTechnicianScoped) { setLinkedInvoiceNumber(null); return; }
    setCheckingInvoice(true);
    ensureTicketInvoice(repair, { depositPaid, taxEnabled, vatRate, nhilGetfundRate, warrantyDays: settings?.warranty_days })
      .then(invoiceNumber => { if (!cancelled) setLinkedInvoiceNumber(invoiceNumber); })
      .catch(() => { if (!cancelled) setLinkedInvoiceNumber(null); })
      .finally(() => { if (!cancelled) setCheckingInvoice(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repair.ticketDbId, readyForInvoice, isTechnicianScoped, depositPaid, taxEnabled, vatRate, nhilGetfundRate, settings?.warranty_days]);

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    onAddNote(repair.id, noteText.trim());
    setNoteText('');
    setAddingNote(false);
  };

  const openPhotoPicker = (stage: RepairMediaStage) => {
    if (!canUploadMedia) return;
    setUploadError('');
    setPromptStage(stage);
    setPhotoSheetOpen(true);
  };

  const pickPhotoSource = (source: 'photos' | 'camera') => {
    setPhotoSheetOpen(false);
    (source === 'camera' ? cameraInputRef : fileInputRef).current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const stage = promptStage ?? statusToMediaStage(repair.status);
    e.target.value = '';
    if (!file || !canUploadMedia) return;
    setUploading(true);
    setUploadError('');
    try {
      await onAddMedia(repair.id, { file, stage, uploadedBy: uploaderName });
    } catch (err) {
      setUploadError(errMessage(err, 'Upload failed. Try again.'));
    } finally {
      setUploading(false);
      setPromptStage(null);
    }
  };

  // Admin-only escape hatch for when a required photo can't be attached
  // (e.g. a storage outage froze every ticket in the queue in Sept 2026).
  // The reason is logged to Internal Notes before the status moves, so a
  // skipped photo is always attributable.
  const canWaivePhoto = user?.role === 'admin' && !!requiredStage && !hasRequiredPhoto && !!repair.ticketDbId;
  const handleWaivePhoto = async () => {
    const reason = waiveReason.trim();
    if (!reason || !repair.ticketDbId || !requiredStage) return;
    setWaiveSaving(true);
    try {
      await addTicketComment(repair.ticketDbId, `[Photo waived: ${REQUIRED_MEDIA_STAGE_LABEL[requiredStage] ?? requiredStage}] ${reason}`, user?.name ?? 'Admin');
      onUpdateStatus(repair.id, upcomingStatus);
      setWaivingPhoto(false);
      setWaiveReason('');
      loadComments();
    } catch (e) {
      alert(errMessage(e, 'Failed to record the reason; the ticket was not advanced.'));
    } finally {
      setWaiveSaving(false);
    }
  };

  const handleAdvanceClick = () => {
    if (requiredStage && !hasRequiredPhoto) { openPhotoPicker(requiredStage); return; }
    onUpdateStatus(repair.id, upcomingStatus);
  };

  const PART_COLORS: Record<string, { bg: string; color: string }> = {
    ordered:   { bg: 'rgba(99,102,241,0.15)', color: '#6366f1' },
    pending:   { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    installed: { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
  };

  return (
    <div className="flex flex-col h-full overflow-hidden"
      style={{ background: 'hsl(var(--card))', borderLeft: '1px solid hsl(var(--border))' }}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-3 shrink-0"
        style={{ borderBottom: '1px solid hsl(var(--border))' }}>
        <span className="text-sm font-bold" style={{ color: 'hsl(var(--foreground))' }}>Ticket Details</span>
        <div className="flex items-center gap-1.5">
          {canPrintReceipt && !isTechnicianScoped && (
            <button onClick={handlePrintReceipt} className="w-7 h-7 flex items-center justify-center rounded-lg"
              style={{ color: 'hsl(var(--muted-foreground))' }}
              title="Print pickup receipt"
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}>
              <Printer className="w-3.5 h-3.5" />
            </button>
          )}
          {canManageTickets && (
            <button onClick={onEdit} className="w-7 h-7 flex items-center justify-center rounded-lg"
              style={{ color: 'hsl(var(--muted-foreground))' }}
              title="Edit"
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {canDeleteTickets && (
            <button onClick={onDelete} className="w-7 h-7 flex items-center justify-center rounded-lg"
              style={{ color: 'hsl(var(--muted-foreground))' }}
              title="Delete"
              onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.background = 'hsl(0 60% 95%)'; el.style.color = 'hsl(0 65% 45%)'; }}
              onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.background = ''; el.style.color = 'hsl(var(--muted-foreground))'; }}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg"
            style={{ color: 'hsl(var(--muted-foreground))' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* ID + device */}
        <div>
          <p className="text-[11px] font-mono mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>{repair.id}</p>
          <p className="text-lg font-bold leading-tight" style={{ color: 'hsl(var(--foreground))' }}>{repair.device}</p>
          <p className="text-xs mt-0.5 mb-2" style={{ color: 'hsl(var(--muted-foreground))' }}>{repair.issue}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
              style={{ background: s.bg, color: s.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
              {s.label}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
              style={isDxOnly
                ? { background: 'rgba(14,165,233,0.12)', color: '#0ea5e9' }
                : isStraightRepair
                ? { background: 'rgba(34,197,94,0.12)', color: '#22c55e' }
                : { background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}>
              {isDxOnly ? <Stethoscope className="w-3 h-3" /> : isStraightRepair ? <Zap className="w-3 h-3" /> : <Scissors className="w-3 h-3" />}
              {isDxOnly ? 'Diagnosis Only' : isStraightRepair ? 'Straight Repair' : 'Full Repair'}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold" style={{ color: 'hsl(var(--foreground))' }}>Progress</p>
            <p className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>Step {Math.min(currentStep + 1, pipeline.length)} of {pipeline.length}</p>
          </div>
          <div className="flex items-center gap-1 mb-2">
            {pipeline.map((step, i) => (
              <div key={step} className="flex-1 h-1.5 rounded-full" style={{ background: currentStep >= i ? '#22c55e' : 'hsl(var(--muted))' }} />
            ))}
          </div>
          <p className="text-xs font-semibold" style={{ color: '#22c55e' }}>
            {pipeline[Math.min(currentStep, pipeline.length - 1)]}
          </p>
        </div>

        {/* Details */}
        <div className="space-y-2.5 pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          {[
            // Technicians see the customer's name (same-model devices are told
            // apart by owner) but not what they're being charged/have paid —
            // that's a reception/admin concern.
            ['Customer', repair.customer],
            ['Technician',     repair.technicians.length ? repair.technicians.map(t => t.name).join(', ') : '—'],
            ['Started',        fmtStarted(repair.started)],
            [isOverdueRepair(repair) ? 'Overdue' : 'ETA', repair.eta || '—'],
            ...(isTechnicianScoped ? [] : [['Estimated Cost', repair.cost || '—']]),
            ...(!isTechnicianScoped && depositPaid > 0 ? [['Deposit Paid', `GHS ${depositPaid.toFixed(2)}`]] : []),
            ['Warranty',       repair.warranty ? 'Yes' : 'No'],
          ].map(([label, value]) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-xs" style={{ color: label === 'Overdue' ? '#ef4444' : 'hsl(var(--muted-foreground))' }}>{label}</span>
              <span className="text-xs font-medium"
                style={{ color: label === 'Overdue' ? '#ef4444' : label === 'Warranty' && value === 'No' ? 'hsl(var(--muted-foreground))' : 'hsl(var(--foreground))' }}>
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* Parts Needed — a technician's suggestion/request list, no stock impact */}
        <div className="pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold" style={{ color: 'hsl(var(--foreground))' }}>Parts Needed</p>
            {!isDone && canUpdateProgress && !addingRecommendedPart && (
              <button
                onClick={() => setAddingRecommendedPart(true)}
                className="text-xs font-medium flex items-center gap-1"
                style={{ color: 'hsl(var(--primary))' }}>
                <Plus className="w-3 h-3" /> Recommend Part
              </button>
            )}
          </div>
          {addingRecommendedPart && (
            <div className="flex gap-2 mb-2">
              <input
                autoFocus
                list="inventory-part-names"
                value={recommendedPartName}
                onChange={e => setRecommendedPartName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecommendedPart(); } }}
                placeholder="e.g. Screen, Battery…"
                className="flex-1 h-9 px-3 rounded-lg text-xs outline-none"
                style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
              <datalist id="inventory-part-names">
                {inventoryParts.map(p => <option key={p.id} value={p.name} />)}
              </datalist>
              <button onClick={handleAddRecommendedPart}
                className="px-3 h-9 rounded-lg text-xs font-semibold text-white"
                style={{ background: 'hsl(var(--primary))' }}>
                Add
              </button>
              <button onClick={() => { setAddingRecommendedPart(false); setRecommendedPartName(''); }}
                className="w-9 h-9 flex items-center justify-center rounded-lg"
                style={{ color: 'hsl(var(--muted-foreground))' }}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {!repair.parts || repair.parts.length === 0 ? (
            <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>No parts recommended yet.</p>
          ) : (
            <div className="space-y-2">
              {repair.parts.map((part, i) => {
                const pc = PART_COLORS[part.status] ?? PART_COLORS.pending;
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl"
                    style={{ background: 'hsl(var(--muted))' }}>
                    <span className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>{part.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize"
                        style={{ background: pc.bg, color: pc.color }}>
                        {part.status.charAt(0).toUpperCase() + part.status.slice(1)}
                      </span>
                      {canUpdateProgress && (
                        <button onClick={() => handleRemoveRecommendedPart(i)}
                          className="w-5 h-5 flex items-center justify-center rounded"
                          style={{ color: 'hsl(var(--muted-foreground))' }}>
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Parts Used — real inventory-linked consumption; stock comes off when the ticket reaches Ready */}
        <div className="pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold" style={{ color: 'hsl(var(--foreground))' }}>Parts Used</p>
            {!isDone && canLogParts && (
              <button
                onClick={() => setShowAddPart(true)}
                className="text-xs font-medium flex items-center gap-1"
                style={{ color: 'hsl(var(--primary))' }}>
                <Plus className="w-3 h-3" /> Add Part
              </button>
            )}
          </div>
          {ticketParts.length === 0 ? (
            <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>No parts used yet.</p>
          ) : (
            <div className="space-y-2">
              {ticketParts.map(part => (
                <div key={part.id} className="flex items-center justify-between px-3 py-2 rounded-xl"
                  style={{ background: 'hsl(var(--muted))' }}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>{part.part_name}</p>
                    <p className="text-[11px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
                      ×{part.quantity} @ ¢{part.unit_cost.toFixed(2)} = ¢{(part.quantity * part.unit_cost).toFixed(2)}
                    </p>
                    {part.substituted_for && (
                      <p className="text-[11px] font-medium" style={{ color: '#b45309' }}>
                        In-house: booked as {part.substituted_for}
                      </p>
                    )}
                  </div>
                  {canLogParts && (
                    <button onClick={() => handleRemoveTicketPart(part)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ color: 'hsl(var(--muted-foreground))' }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Photos */}
        <div className="pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold" style={{ color: 'hsl(var(--foreground))' }}>Photos</p>
            {!isDone && canUploadMedia && (
              <button
                onClick={() => openPhotoPicker(statusToMediaStage(repair.status))}
                disabled={uploading}
                className="text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                style={{ color: 'hsl(var(--primary))' }}>
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                {uploading ? 'Uploading…' : 'Add Photo'}
              </button>
            )}
          </div>

          {requiredStage && !hasRequiredPhoto && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3"
              style={{ background: 'rgba(245,158,11,0.1)', color: '#b45309' }}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px] font-medium">
                {canUploadMedia ? 'Attach a' : 'An assigned technician must attach a'} {REQUIRED_MEDIA_STAGE_LABEL[requiredStage] || 'required'} photo before this ticket can advance.
              </span>
            </div>
          )}

          {uploadError && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3"
              style={{ background: 'rgba(239,68,68,0.1)', color: '#dc2626' }}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px] font-medium">{uploadError}</span>
            </div>
          )}

          {media.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {media.map(m => {
                const canDelete = canUpdateProgress || (!!uploaderName && m.uploadedBy === uploaderName);
                const resolvedUrl = (m.path && signedMediaUrls[m.path]) || m.url;
                return (
                  <a key={m.id} href={resolvedUrl} target="_blank" rel="noreferrer"
                    className="relative aspect-square rounded-lg overflow-hidden block group/photo"
                    style={{ background: 'hsl(var(--muted))' }}
                    title={m.caption || m.fileName}>
                    {m.type === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-5 h-5" style={{ color: 'hsl(var(--muted-foreground))' }} />
                      </div>
                    ) : (
                      <img src={resolvedUrl} alt={m.caption || m.fileName} className="w-full h-full object-cover" />
                    )}
                    <span className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[8px] font-semibold text-center text-white capitalize truncate"
                      style={{ background: 'rgba(0,0,0,0.55)' }}>
                      {m.stage.replace('_', ' ')}
                    </span>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (window.confirm('Delete this photo?')) onRemoveMedia(repair.id, m.id);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover/photo:opacity-100 transition-opacity"
                        style={{ background: 'rgba(0,0,0,0.6)' }}
                        title="Delete photo"
                      >
                        <Trash2 className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </a>
                );
              })}
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>No photos yet.</p>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={!canUploadMedia}
            className="hidden"
            onChange={handleFileChange}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={!canUploadMedia}
            capture="environment"
            className="hidden"
            onChange={handleFileChange}
          />
          {photoSheetOpen && (
            <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.5)' }}
              onClick={() => { setPhotoSheetOpen(false); setPromptStage(null); }}>
              <div className="w-full sm:w-auto sm:min-w-[320px] rounded-t-3xl sm:rounded-3xl px-8 pt-6 pb-8"
                style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-center gap-10">
                  {([
                    { key: 'photos', label: 'Photos', Icon: Images, color: '#3b82f6' },
                    { key: 'camera', label: 'Camera', Icon: Camera, color: 'hsl(var(--foreground))' },
                  ] as const).map(({ key, label, Icon, color }) => (
                    <button key={key} onClick={() => pickPhotoSource(key)}
                      className="flex flex-col items-center gap-2">
                      <span className="w-20 h-20 rounded-full flex items-center justify-center"
                        style={{ background: 'hsl(var(--muted))' }}>
                        <Icon className="w-9 h-9" style={{ color }} />
                      </span>
                      <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold" style={{ color: 'hsl(var(--foreground))' }}>Technician Notes</p>
            {canUpdateProgress && (
              <button onClick={() => setAddingNote(true)} className="text-xs font-medium flex items-center gap-1"
                style={{ color: 'hsl(var(--primary))' }}>
                <Plus className="w-3 h-3" />Add Note
              </button>
            )}
          </div>
          {(repair.notes ?? []).length > 0 && (
            <ul className="space-y-1.5 mb-3">
              {repair.notes.map((note, i) => (
                <li key={i} className="flex gap-2 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  <span className="shrink-0 mt-0.5">•</span><span>{note}</span>
                </li>
              ))}
            </ul>
          )}
          {addingNote && (
            <div className="space-y-2">
              <textarea autoFocus rows={3} value={noteText} onChange={e => setNoteText(e.target.value)}
                placeholder="Add a technician note..."
                className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setAddingNote(false); setNoteText(''); }}
                  className="px-3 py-1.5 text-xs rounded-lg"
                  style={{ color: 'hsl(var(--muted-foreground))', border: '1px solid hsl(var(--border))' }}>
                  Cancel
                </button>
                <button onClick={handleAddNote} className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg"
                  style={{ background: 'hsl(var(--primary))' }}>
                  Save
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Internal Notes — staff-to-staff only, never shown to the customer.
            For flagging something mid-job (an extra issue found, a part
            that'll cost more) so reception can decide on pricing and be the
            one to tell the customer — technicians don't contact customers
            directly. */}
        <div className="pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-bold" style={{ color: 'hsl(var(--foreground))' }}>Internal Notes</p>
              <p className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>Staff only, never shown to the customer</p>
            </div>
            <button onClick={() => setAddingComment(true)} className="text-xs font-medium flex items-center gap-1"
              style={{ color: 'hsl(var(--primary))' }}>
              <Plus className="w-3 h-3" />Add Note
            </button>
          </div>
          {internalComments.length > 0 && (
            <ul className="space-y-2 mb-3">
              {internalComments.map(c => (
                <li key={c.id} className="text-xs rounded-lg px-3 py-2" style={{ background: 'hsl(var(--muted))' }}>
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{c.authorName}</span>
                    <span className="shrink-0" style={{ color: 'hsl(var(--muted-foreground))' }}>{fmtStarted(c.createdAt)}</span>
                  </div>
                  <p style={{ color: 'hsl(var(--muted-foreground))' }}>{c.body}</p>
                </li>
              ))}
            </ul>
          )}
          {addingComment && (
            <div className="space-y-2">
              <textarea autoFocus rows={3} value={commentText} onChange={e => setCommentText(e.target.value)}
                placeholder="e.g. Found a cracked digitizer under the screen too, needs an extra part, let the customer know before we proceed…"
                className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setAddingComment(false); setCommentText(''); }}
                  className="px-3 py-1.5 text-xs rounded-lg"
                  style={{ color: 'hsl(var(--muted-foreground))', border: '1px solid hsl(var(--border))' }}>
                  Cancel
                </button>
                <button onClick={handleAddComment} disabled={commentSaving}
                  className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-60"
                  style={{ background: 'hsl(var(--primary))' }}>
                  {commentSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      {isClosedDiagnosis ? (
        <div className="px-5 py-4 space-y-2 shrink-0" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          {/* Invoicing/billing state is a reception/admin concern — not shown on a technician's view of the ticket. */}
          {!isTechnicianScoped && (linkedInvoiceNumber ? (
            <div className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Invoice {linkedInvoiceNumber} created
            </div>
          ) : checkingInvoice ? (
            <div className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing invoice…
            </div>
          ) : canManageTickets ? (
            <button
              onClick={() => setShowInvoiceModal(true)}
              className="w-full h-10 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
              style={{ background: '#22c55e' }}>
              Create Invoice
            </button>
          ) : (
            <p className="text-[11px] text-center py-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
              Only a receptionist or admin can create the invoice for this ticket.
            </p>
          ))}
          {canUpdateProgress ? (
            <>
              <button
                onClick={() => onProceedToRepair(repair.id)}
                className="w-full h-10 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                style={{ background: '#0ea5e9' }}>
                <ArrowRightCircle className="w-4 h-4" />
                Reopen for Repair
              </button>
              <p className="text-[10px] text-center" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Customer changed their mind and wants to go ahead with the repair.
              </p>
            </>
          ) : (
            <p className="text-[11px] text-center" style={{ color: 'hsl(var(--muted-foreground))' }}>
              Only a technician or admin can reopen this for repair.
            </p>
          )}
        </div>
      ) : isAwaitingDecision ? (
        <div className="px-5 py-4 space-y-2 shrink-0" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          {canUpdateProgress ? (
            <>
              <p className="text-[11px] text-center mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Diagnosis complete. What's next?
              </p>
              <button
                onClick={() => onProceedToRepair(repair.id)}
                className="w-full h-10 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                style={{ background: 'hsl(var(--primary))' }}>
                <ArrowRightCircle className="w-4 h-4" />
                Proceed to Repair
              </button>
              <button
                onClick={() => onCloseDiagnosisOnly(repair.id)}
                className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5"
                style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}>
                <XCircle className="w-3.5 h-3.5" />
                Close (Diagnosis Only)
              </button>
            </>
          ) : (
            <p className="text-[11px] text-center" style={{ color: 'hsl(var(--muted-foreground))' }}>
              Diagnosis complete. Awaiting a technician or admin to proceed to repair or close it.
            </p>
          )}
        </div>
      ) : !isDone && (
        <div className="px-5 py-4 space-y-2 shrink-0" style={{ borderTop: '1px solid hsl(var(--border))' }}>
          {/* Invoicing/billing state is a reception/admin concern — not shown on a technician's view of the ticket. */}
          {!isTechnicianScoped && readyForInvoice && (
            linkedInvoiceNumber ? (
              <div className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Invoice {linkedInvoiceNumber} created
              </div>
            ) : checkingInvoice ? (
              <div className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing invoice…
              </div>
            ) : canManageTickets ? (
              <button
                onClick={() => setShowInvoiceModal(true)}
                className="w-full h-10 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                style={{ background: '#22c55e' }}>
                Create Invoice
              </button>
            ) : (
              <p className="text-[11px] text-center py-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Only a receptionist or admin can create the invoice for this ticket.
              </p>
            )
          )}
          {(isMarkCollectedStep ? canMarkCollected : canUpdateProgress) ? (
            <button
              onClick={handleAdvanceClick}
              disabled={uploading || (!!requiredStage && !hasRequiredPhoto && !canUploadMedia)}
              className="w-full h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              style={requiredStage && !hasRequiredPhoto
                ? { background: 'rgba(245,158,11,0.15)', color: '#b45309' }
                : { background: 'hsl(var(--foreground))', color: 'hsl(var(--background))' }}>
              {requiredStage && !hasRequiredPhoto ? <Camera className="w-4 h-4" /> : null}
              {requiredStage && !hasRequiredPhoto
                ? (canUploadMedia ? 'Add Photo to Continue' : 'Awaiting Technician Photo')
                : nextAction(repair.status, repair.jobType)}
            </button>
          ) : (
            <p className="text-[11px] text-center py-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {isMarkCollectedStep
                ? 'Only reception or an admin can mark this ticket collected.'
                : "Only a technician or admin can update this ticket's progress."}
            </p>
          )}
          {canWaivePhoto && (
            waivingPhoto ? (
              <div className="space-y-2 pt-1">
                <p className="text-[11px] font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                  Why is this ticket continuing without the photo? This is recorded in Internal Notes.
                </p>
                <textarea autoFocus rows={2} value={waiveReason} onChange={e => setWaiveReason(e.target.value)}
                  placeholder="e.g. Photo uploads are down; technician confirmed the device condition in person"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                  style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setWaivingPhoto(false); setWaiveReason(''); }}
                    className="px-3 py-1.5 text-xs rounded-lg"
                    style={{ color: 'hsl(var(--muted-foreground))', border: '1px solid hsl(var(--border))' }}>
                    Cancel
                  </button>
                  <button onClick={handleWaivePhoto} disabled={!waiveReason.trim() || waiveSaving}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
                    style={{ background: '#b45309' }}>
                    {waiveSaving ? 'Saving…' : `Continue: ${nextAction(repair.status, repair.jobType)}`}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setWaivingPhoto(true)}
                className="w-full h-9 rounded-xl text-xs font-semibold"
                style={{ border: '1px solid rgba(180,83,9,0.35)', color: '#b45309' }}>
                Continue without photo (admin)
              </button>
            )
          )}
          {canUpdateProgress && (
            <button onClick={() => setAddingNote(true)}
              className="w-full h-9 rounded-xl text-xs font-semibold"
              style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}>
              Add Note
            </button>
          )}
          {(canUpdateProgress || canManageTickets) && (
            discontinuing ? (
              <div className="space-y-2 pt-1">
                <p className="text-[11px] font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                  Why is this being discontinued?
                </p>
                <textarea autoFocus rows={2} value={discontinueReason} onChange={e => setDiscontinueReason(e.target.value)}
                  placeholder="e.g. Customer declined the repair after we found a cracked board mid-job…"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                  style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setDiscontinuing(false); setDiscontinueReason(''); }}
                    className="px-3 py-1.5 text-xs rounded-lg"
                    style={{ color: 'hsl(var(--muted-foreground))', border: '1px solid hsl(var(--border))' }}>
                    Cancel
                  </button>
                  <button onClick={handleConfirmDiscontinue} disabled={!discontinueReason.trim()}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
                    style={{ background: '#ef4444' }}>
                    Confirm Discontinue
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setDiscontinuing(true)}
                className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5"
                style={{ border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
                <XCircle className="w-3.5 h-3.5" /> Discontinue
              </button>
            )
          )}
          {canUpdateProgress && (
            requestingReassign ? (
              <div className="space-y-2 pt-1">
                <p className="text-[11px] font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                  Why does this need reassigning?
                </p>
                <textarea autoFocus rows={2} value={reassignReason} onChange={e => setReassignReason(e.target.value)}
                  placeholder="e.g. I'm swamped today, can Kofi take this one over?"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                  style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setRequestingReassign(false); setReassignReason(''); }}
                    className="px-3 py-1.5 text-xs rounded-lg"
                    style={{ color: 'hsl(var(--muted-foreground))', border: '1px solid hsl(var(--border))' }}>
                    Cancel
                  </button>
                  <button onClick={handleRequestReassign} disabled={!reassignReason.trim() || reassignSaving}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
                    style={{ background: 'hsl(var(--primary))' }}>
                    {reassignSaving ? 'Sending…' : 'Send Request'}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setRequestingReassign(true)}
                className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5"
                style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}>
                <Users className="w-3.5 h-3.5" /> Request Reassignment
              </button>
            )
          )}
          {canUpdateProgress && ['in_progress', 'parts_pending'].includes(repair.status) && (
            reportingExtra ? (
              <div className="space-y-2 pt-1">
                <p className="text-[11px] font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                  What did you find, and what will it cost?
                </p>
                <textarea autoFocus rows={2} value={extraReason} onChange={e => setExtraReason(e.target.value)}
                  placeholder="e.g. Found a cracked logic board while replacing the screen, needs a new one"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                  style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                <input type="number" min="0" step="0.01" value={extraAmount} onChange={e => setExtraAmount(e.target.value)}
                  placeholder="Extra cost (GHS), optional"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none"
                  style={{ background: 'hsl(var(--muted))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setReportingExtra(false); setExtraReason(''); setExtraAmount(''); }}
                    className="px-3 py-1.5 text-xs rounded-lg"
                    style={{ color: 'hsl(var(--muted-foreground))', border: '1px solid hsl(var(--border))' }}>
                    Cancel
                  </button>
                  <button onClick={handleRequestApproval} disabled={!extraReason.trim() || extraSaving}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
                    style={{ background: 'hsl(var(--primary))' }}>
                    {extraSaving ? 'Sending…' : 'Send to Reception'}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setReportingExtra(true)}
                className="w-full h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5"
                style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'hsl(var(--muted))'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}>
                <AlertTriangle className="w-3.5 h-3.5" /> Report Additional Issue
              </button>
            )
          )}
        </div>
      )}

      {showInvoiceModal && (
        <CreateTicketInvoiceModal
          repair={repair}
          depositPaid={depositPaid}
          onClose={() => setShowInvoiceModal(false)}
          onCreated={invoiceNumber => setLinkedInvoiceNumber(invoiceNumber)}
        />
      )}

      {showAddPart && repair.ticketDbId && (
        <AddTicketPartModal
          ticketDbId={repair.ticketDbId}
          onClose={() => setShowAddPart(false)}
          onAdded={loadTicketParts}
        />
      )}
    </div>
  );
}

// ── Board ─────────────────────────────────────────────────────────────────

export default function RepairsBoard() {
  const { setPageTitle } = usePageTitle();
  const { repairs, loading, error, reload, add, updateStatus, addNote, addMedia, removeMedia, patchRepair, remove } = useRepairs();
  const { technicians } = useTechnicians();
  const { user } = useAuth();
  const isTechnician = user?.role === 'technician';
  const perms = user?.permissions ?? [];
  // Gates New Ticket, Edit, Create Invoice, and the reassignment banner —
  // was hardcoded to admin/receptionist, which silently dead-ended every
  // custom role (sales_manager, manager, finance) that has the matching
  // tickets/invoices permission but isn't literally one of those two roles.
  const canManageTickets = !isTechnician && (user?.role === 'admin' || perms.includes('tickets:edit') || perms.includes('tickets:create') || perms.includes('invoices:create'));
  // Mirrors prevent_unauthorized_ticket_status_change(): admin, or any role
  // with scope_tickets_to_technician set (an editable per-role flag in
  // Settings > Roles, not exclusive to the literal 'technician' role id —
  // a custom scoped-tech role would pass the DB trigger but hit a hidden
  // progress UI under the old hardcoded check).
  const canUpdateProgress = user?.role === 'admin' || isTechnician;
  // Mirrors the DB's ready→completed gate: the pickup handoff is reception's
  // call (or any custom role explicitly granted it), never the technician's.
  const canMarkCollected = user?.role === 'admin' || perms.includes('tickets:collect');
  const canDeleteTickets = !isTechnician && (user?.role === 'admin' || perms.includes('tickets:delete'));
  const { requests: reassignmentRequests, resolve: resolveReassignment } = useReassignmentRequests();
  const { requests: approvalRequests, resolve: resolveApproval } = useApprovalRequests();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRepair, setEditingRepair] = useState<Repair | null>(null);

  const selected = selectedId ? (repairs.find(r => r.id === selectedId) ?? null) : null;

  useEffect(() => { setPage(1); }, [query, filter, techFilter, dateRange]);

  useEffect(() => {
    if (!isTechnician) return;
    setTechFilter('all');
    setFilter(current => current === 'unassigned' ? 'all' : current);
  }, [isTechnician]);

  useEffect(() => {
    setPageTitle({
      title: isTechnician ? 'My Tickets' : 'Tickets',
      subtitle: isTechnician
        ? 'Only repair jobs assigned to you are shown here'
        : 'Available jobs, assign technicians and track diagnosis & repair progress',
      hideDefaultAction: true,
    });
    return () => setPageTitle({ title: 'Dashboard' });
  }, [isTechnician, setPageTitle]);

  const unassigned   = useMemo(() => repairs.filter(r => isActiveRepairStatus(r.status) && !r.technicians.length), [repairs]);
  const inDiagnosis  = useMemo(() => repairs.filter(r => isActiveRepairStatus(r.status) && isDiagnosisStage(r)), [repairs]);
  const inRepair     = useMemo(() => repairs.filter(r => isActiveRepairStatus(r.status) && !isDiagnosisStage(r) && r.status !== 'ready'), [repairs]);
  const ready        = useMemo(() => repairs.filter(r => r.status === 'ready'), [repairs]);
  const overdue      = useMemo(() => repairs.filter(isOverdueRepair), [repairs]);

  const filterTabs = isTechnician ? FILTER_TABS.filter(tab => tab.key !== 'unassigned') : FILTER_TABS;

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const from = dateRange.from ? new Date(dateRange.from + 'T00:00') : null;
    const to   = dateRange.to   ? new Date(dateRange.to   + 'T23:59:59') : null;
    return repairs.filter(r => {
      if (filter === 'unassigned') {
        if (r.technicians.length) return false;
      } else if (filter === 'in_queue') {
        // Unlike the Technicians module (which has no separate Ready
        // bucket, so it lumps Ready into Queue), this page has its own
        // Ready tab — Queue here means "not yet started and not ready",
        // or the two tabs would show overlapping tickets.
        if (r.status === 'in_progress' || r.status === 'ready' || !isActiveRepairStatus(r.status)) return false;
      } else {
        const matchFilter = filter === 'all' || STATUS[r.status]?.filterKey === filter;
        if (!matchFilter) return false;
      }
      if (techFilter !== 'all' && !r.technicians.some(t => t.id === techFilter)) return false;
      if (q && !r.id.toLowerCase().includes(q)
            && !r.device.toLowerCase().includes(q)
            && !r.issue.toLowerCase().includes(q)
            && (isTechnician || !r.customer.toLowerCase().includes(q))) return false;
      if (from || to) {
        const d = new Date(r.createdAt ?? r.id);
        if (from && d < from) return false;
        if (to   && d > to)   return false;
      }
      return true;
    });
  }, [repairs, filter, techFilter, query, dateRange, isTechnician]);

  const paged = useMemo(() =>
    filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
  [filtered, page]);

  const handleUpdateStatus = (id: string, status: RepairStatus) => {
    updateStatus(id, status);
    if (status === 'completed') setSelectedId(null);
  };

  const handleProceedToRepair = (id: string) => {
    patchRepair(id, {
      jobType: 'diagnosis_to_repair',
      status: 'parts_pending',
      serviceStage: statusToServiceStage('parts_pending', 'diagnosis_to_repair'),
    });
  };

  const handleCloseDiagnosisOnly = (id: string) => {
    patchRepair(id, {
      jobType: 'diagnosis_only',
      status: 'diagnosis_only_closed',
      serviceStage: statusToServiceStage('diagnosis_only_closed', 'diagnosis_only'),
      completedDate: new Date().toISOString(),
    });
  };

  // Ends the job without a completed repair — declined after diagnosis,
  // mid-repair, or on a return visit — at whatever stage/job type it's
  // currently in (unlike Close-Diagnosis-Only, which only ever applies
  // before repair work starts). Deliberately doesn't touch completedDate:
  // isDoneStatus()/avgTurnaround/revenue elsewhere already only count
  // 'completed'/'diagnosis_only_closed', so a discontinued ticket correctly
  // never counts as revenue — any diagnosis fee already paid stays recorded
  // in payments regardless, and readyForInvoice already accounts for that.
  const handleDiscontinue = (id: string, reason: string) => {
    const target = repairs.find(r => r.id === id);
    if (!target) return;
    patchRepair(id, { status: 'cancelled', serviceStage: 'closed' });
    if (target.ticketDbId) {
      addTicketComment(target.ticketDbId, `Discontinued: ${reason}`, user?.name ?? 'Staff').catch(() => {});
    }
    setSelectedId(null);
  };

  const handleDelete = (repair: Repair) => {
    if (!window.confirm(`Delete ${repair.id} (${repair.device})? This can't be undone.`)) return;
    remove(repair.id);
    setSelectedId(null);
  };

  const STATS = [
    { label: 'UNASSIGNED',     value: String(unassigned.length),  icon: UserX,        border: '#f59e0b' },
    { label: 'IN DIAGNOSIS',   value: String(inDiagnosis.length),  icon: Stethoscope,  border: '#0ea5e9' },
    { label: 'IN REPAIR',      value: String(inRepair.length),     icon: Scissors,     border: '#7c3aed' },
    { label: 'READY FOR PICKUP', value: String(ready.length),      icon: CheckCircle2, border: '#22c55e' },
    { label: 'OVERDUE',        value: String(overdue.length),      icon: AlertCircle,  border: '#ef4444' },
  ].filter(card => !isTechnician || card.label !== 'UNASSIGNED');

  const cols = selected ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className="flex h-full -m-6 overflow-hidden">
      {/* Scrollable left section */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5 min-w-0">
        {/* Stat cards */}
        <div className={`grid grid-cols-2 ${isTechnician ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-4`}>
          {STATS.map(card => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="rounded-2xl p-5 relative overflow-hidden"
                style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}>
                <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: card.border }} />
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-2"
                      style={{ color: 'hsl(var(--muted-foreground))' }}>{card.label}</p>
                    <p className={`font-bold ${('large' in card && card.large) ? 'text-xl' : 'text-3xl'}`}
                      style={{ color: 'hsl(var(--foreground))' }}>{card.value}</p>
                  </div>
                  <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'hsl(var(--muted-foreground))' }} />
                </div>
              </div>
            );
          })}
        </div>

        {canManageTickets && (
          <ReassignmentRequestsBanner
            requests={reassignmentRequests}
            technicians={technicians}
            onReassign={(ticketId, tech) => patchRepair(ticketId, { technicians: [{ id: tech.id, name: tech.name }] })}
            onResolve={resolveReassignment}
          />
        )}

        {canManageTickets && (
          <ApprovalRequestsBanner
            requests={approvalRequests}
            onResolve={(commentId, ticketId, decision) => resolveApproval(commentId, ticketId, decision, user?.name ?? 'Staff')}
          />
        )}
        <StaleTicketsBanner repairs={repairs} onSelect={setSelectedId} />
        {/* Oversight view — admin only. sales_manager/manager also reach this
            board but weren't part of the intended audience when this was
            reinstated; receptionist gets the same reminder from their own
            portal's Tickets tab instead (below), not from here. */}
        {user?.role === 'admin' && <EtaRemindersBanner repairs={repairs} onSelect={setSelectedId} />}

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <SearchDropdown
            query={query}
            onQueryChange={setQuery}
            suggestions={query.trim() ? filtered.map(r => ({
              id: r.id,
              primary: r.id,
              secondary: isTechnician ? r.device : `${r.device} · ${r.customer}`,
              meta: r.issue.slice(0, 36),
              badge: { label: STATUS[r.status]?.label ?? r.status, bg: STATUS[r.status]?.bg ?? '', color: STATUS[r.status]?.color ?? '' },
            })) : []}
            onSelect={item => setQuery(item.id)}
            placeholder="Search tickets…"
            width={220}
          />

          <div className="flex items-center gap-1.5 flex-wrap">
            {filterTabs.map(tab => (
              <button key={tab.key} onClick={() => setFilter(tab.key)}
                className="px-3 h-8 rounded-full text-xs font-semibold transition-all"
                style={{
                  background: filter === tab.key ? 'hsl(var(--foreground))' : 'transparent',
                  color: filter === tab.key ? 'hsl(var(--background))' : 'hsl(var(--muted-foreground))',
                  border: `1px solid ${filter === tab.key ? 'transparent' : 'hsl(var(--border))'}`,
                }}>
                {tab.label}
              </button>
            ))}
            {!isTechnician && technicians.length > 0 && (
              <select value={techFilter} onChange={e => setTechFilter(e.target.value)}
                className="h-8 px-3 rounded-lg text-xs font-semibold outline-none"
                style={{ background: 'transparent', border: '1px solid hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                <option value="all">All Technicians</option>
                {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            <DateRangePicker value={dateRange} onChange={setDateRange} label="Received date" />
          </div>

          {canManageTickets && (
            <button
              onClick={() => setShowAddModal(true)}
              className="ml-auto flex items-center gap-1.5 px-4 h-9 rounded-xl text-xs font-bold"
              style={{ background: 'hsl(var(--foreground))', color: 'hsl(var(--background))' }}>
              <Plus className="w-3.5 h-3.5" /> New Ticket
            </button>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <p className="py-16 text-center text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Loading…</p>
        ) : error ? (
          <div className="py-16 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto mb-2" style={{ color: '#ef4444' }} />
            <p className="text-xs font-semibold" style={{ color: '#ef4444' }}>{error}</p>
            <p className="text-[11px] mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>No cached tickets are displayed when loading fails.</p>
            <button onClick={reload} className="mt-3 px-3 h-8 rounded-lg text-xs font-semibold text-white" style={{ background: 'hsl(var(--primary))' }}>Try again</button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
            {isTechnician ? 'No tickets are currently assigned to you.' : 'No tickets match.'}
          </p>
        ) : (
          <>
            <div className={`grid ${cols} gap-3`}>
              {paged.map(r => (
                <RepairCard
                  key={r.id}
                  repair={r}
                  onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                  selected={r.id === selectedId}
                />
              ))}
            </div>
            <Pagination
              page={page}
              pageCount={Math.ceil(filtered.length / PAGE_SIZE)}
              total={filtered.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="fixed inset-0 z-40 lg:relative lg:inset-auto lg:z-auto lg:w-80 shrink-0 h-full overflow-hidden border-l"
          style={{ borderColor: 'hsl(var(--border))' }}>
          <RepairDetailPanel
            repair={selected}
            onClose={() => setSelectedId(null)}
            onUpdateStatus={handleUpdateStatus}
            onAddNote={addNote}
            onAddMedia={addMedia}
            onRemoveMedia={removeMedia}
            uploaderName={user?.name}
            canManageTickets={canManageTickets}
            canUpdateProgress={canUpdateProgress}
            canMarkCollected={canMarkCollected}
            canDeleteTickets={canDeleteTickets}
            onProceedToRepair={handleProceedToRepair}
            onCloseDiagnosisOnly={handleCloseDiagnosisOnly}
            onDiscontinue={handleDiscontinue}
            onPatchParts={(id, parts) => patchRepair(id, { parts })}
            onEdit={() => setEditingRepair(selected)}
            onDelete={() => handleDelete(selected)}
          />
        </div>
      )}

      {showAddModal && (
        <AddRepairModal
          onSave={add}
          onClose={() => setShowAddModal(false)}
          repairs={repairs}
        />
      )}

      {editingRepair && (
        <AddRepairModal
          onSave={add}
          onUpdate={patchRepair}
          initial={editingRepair}
          onClose={() => setEditingRepair(null)}
          repairs={repairs}
        />
      )}
    </div>
  );
}
